import { z } from 'zod';

/**
 * Wire contract for the animated weather widget rendered by
 * `apps/web/src/components/chat/tools/weather-widget`. Mirrors the v3.1
 * payload shape from the assistant-ui reference at
 * `apps/docs/components/tool-ui/weather-widget/schema-runtime.ts` so the
 * widget files can be dropped in unmodified.
 *
 * The `get_weather` backend tool returns a `WeatherWidgetPayload` from its
 * `execute()` — the frontend toolkit at `apps/web/src/lib/toolkit.tsx`
 * spreads it straight into `<WeatherWidget {...payload} />`.
 */

export const WeatherConditionCode = z.enum([
  'clear',
  'partly-cloudy',
  'cloudy',
  'overcast',
  'fog',
  'drizzle',
  'rain',
  'heavy-rain',
  'thunderstorm',
  'snow',
  'sleet',
  'hail',
  'windy',
]);
export type WeatherConditionCode = z.infer<typeof WeatherConditionCode>;

export const TemperatureUnit = z.enum(['celsius', 'fahrenheit']);
export type TemperatureUnit = z.infer<typeof TemperatureUnit>;

export const PrecipitationLevel = z.enum(['none', 'light', 'moderate', 'heavy']);
export type PrecipitationLevel = z.infer<typeof PrecipitationLevel>;

/**
 * The reference uses a 12-step integer bucket (0..11) so the rendering
 * shader can index into a precomputed lookup table for time-of-day
 * lighting. Our backend leaves this undefined and provides
 * `localTimeOfDay` (0..1) instead; the widget converts internally.
 */
export const TimeBucket = z.number().int().min(0).max(11);
export type TimeBucket = z.infer<typeof TimeBucket>;

export const ForecastDay = z.object({
  label: z.string(),
  conditionCode: WeatherConditionCode,
  tempMin: z.number(),
  tempMax: z.number(),
});
export type ForecastDay = z.infer<typeof ForecastDay>;

export const WeatherWidgetCurrent = z.object({
  conditionCode: WeatherConditionCode,
  temperature: z.number(),
  tempMin: z.number(),
  tempMax: z.number(),
  windSpeed: z.number().optional(),
  precipitationLevel: PrecipitationLevel.optional(),
  visibility: z.number().optional(),
});
export type WeatherWidgetCurrent = z.infer<typeof WeatherWidgetCurrent>;

export const WeatherWidgetTime = z.object({
  timeBucket: TimeBucket.optional(),
  /** 0..1 fraction of the day in the location's local clock. */
  localTimeOfDay: z.number().min(0).max(1).optional(),
});
export type WeatherWidgetTime = z.infer<typeof WeatherWidgetTime>;

export const WeatherWidgetLocation = z.object({
  name: z.string(),
});
export type WeatherWidgetLocation = z.infer<typeof WeatherWidgetLocation>;

export const WeatherWidgetPayload = z.object({
  version: z.literal('3.1'),
  id: z.string(),
  location: WeatherWidgetLocation,
  units: z.object({
    temperature: TemperatureUnit,
  }),
  current: WeatherWidgetCurrent,
  forecast: z.array(ForecastDay),
  time: WeatherWidgetTime,
  updatedAt: z.string().optional(),
});
export type WeatherWidgetPayload = z.infer<typeof WeatherWidgetPayload>;
